import { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Tabs, Tab, Breadcrumbs, Typography, Container, Paper, TextField, RadioGroup, FormControlLabel, Radio, TextareaAutosize } from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    ClassicEditor, AccessibilityHelp, Alignment, Autoformat, AutoLink, Autosave, BlockQuote, Bold, Code, CodeBlock, Essentials, FindAndReplace, FontBackgroundColor, FontColor, FontFamily, FontSize, GeneralHtmlSupport, Heading, Highlight, HorizontalLine, Indent, IndentBlock, Italic, Link, PageBreak, Paragraph, RemoveFormat, SelectAll, SpecialCharacters, SpecialCharactersArrows, SpecialCharactersCurrency, SpecialCharactersEssentials, SpecialCharactersLatin, SpecialCharactersMathematical, SpecialCharactersText, Strikethrough, Style, Subscript, Superscript, Table, TableCaption, TableCellProperties, TableColumnResize, TableProperties, TableToolbar, TextTransformation, Underline, Undo,
    List,
    Input
} from 'ckeditor5';

import { ProjectContext } from '../../../../providers/projectProvider';
import { useToaster } from '../../../../providers/ToasterProvider';
import { getById, updateById } from '../../../../services/configurationService';

import RadiusButton from '../../../../components/RadiusButton';
import { MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ARROW_LEFT from '../../../../assets/images/back.svg';
import './InstructionsUpload.scss';

export const InstructionsUpload = () => {
    const params = useParams();
    const projectName = params?.projectName;
    const navigate = useNavigate();
    const location = useLocation();
    const { projectDetails } = useContext(ProjectContext);
    const [drive, setDrive] = useState({
        id: null,
        driveName: 'Drive name'
    });
    const [course, setCourse] = useState({
        id: null,
        courseName: 'Course name'
    });

    const [tabIndex, setTabIndex] = useState(0);
    const [configuration, setConfiguration] = useState(null);
    const [initInstruction, setInitInstruction] = useState(null);
    const [instruction, setInstruction] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [editor, setEditor] = useState(null);
    const { addNotification } = useToaster();

    let editorConfig = {
        toolbar: {
            items: [
                'undo', 'redo', '|', 'findAndReplace', 'selectAll', '|', 'heading', '|', 'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|', 'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', 'code', 'removeFormat', '|', 'numberedList', 'bulletedList', 'indent', 'outdent', '|', 'horizontalLine', 'pageBreak', 'link', 'insertTable', 'highlight', 'blockQuote', '|', 'alignment', '|', 'accessibilityHelp'
            ],
            shouldNotGroupWhenFull: true
        },
        plugins: [AccessibilityHelp, Alignment, Autoformat, AutoLink, Autosave, BlockQuote, Bold, Code, CodeBlock, Essentials, FindAndReplace, FontBackgroundColor, FontColor, FontFamily, FontSize, GeneralHtmlSupport, Heading, Highlight, HorizontalLine, Indent, IndentBlock, List, Italic, Link, PageBreak, Paragraph, RemoveFormat, SelectAll, SpecialCharacters, SpecialCharactersArrows, SpecialCharactersCurrency, SpecialCharactersEssentials, SpecialCharactersLatin, SpecialCharactersMathematical, SpecialCharactersText, Strikethrough, Style, Subscript, Superscript, Table, TableCaption, TableCellProperties, TableColumnResize, TableProperties, TableToolbar, TextTransformation, Underline, Undo
        ],
        fontFamily: {
            supportAllValues: true
        },
        fontSize: {
            options: [10, 12, 14, 'default', 18, 20, 22],
            supportAllValues: true
        },
        heading: {
            options: [
                {
                    model: 'paragraph',
                    title: 'Paragraph',
                    class: 'ck-heading_paragraph'
                },
                {
                    model: 'heading1',
                    view: 'h1',
                    title: 'Heading 1',
                    class: 'ck-heading_heading1'
                },
                {
                    model: 'heading2',
                    view: 'h2',
                    title: 'Heading 2',
                    class: 'ck-heading_heading2'
                },
                {
                    model: 'heading3',
                    view: 'h3',
                    title: 'Heading 3',
                    class: 'ck-heading_heading3'
                },
                {
                    model: 'heading4',
                    view: 'h4',
                    title: 'Heading 4',
                    class: 'ck-heading_heading4'
                },
                {
                    model: 'heading5',
                    view: 'h5',
                    title: 'Heading 5',
                    class: 'ck-heading_heading5'
                },
                {
                    model: 'heading6',
                    view: 'h6',
                    title: 'Heading 6',
                    class: 'ck-heading_heading6'
                }
            ]
        },
        htmlSupport: {
            allow: [
                {
                    name: /^.*$/,
                    styles: true,
                    attributes: true,
                    classes: true
                }
            ]
        },
        initialData: null,
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            decorators: {
                toggleDownloadable: {
                    mode: 'manual',
                    label: 'Downloadable',
                    attributes: {
                        download: 'file'
                    }
                }
            }
        },
        placeholder: 'Enter instructions here.',
        style: {
            definitions: [
                {
                    name: 'Article category',
                    element: 'h3',
                    classes: ['category']
                },
                {
                    name: 'Title',
                    element: 'h2',
                    classes: ['document-title']
                },
                {
                    name: 'Subtitle',
                    element: 'h3',
                    classes: ['document-subtitle']
                },
                {
                    name: 'Info box',
                    element: 'p',
                    classes: ['info-box']
                },
                {
                    name: 'Side quote',
                    element: 'blockquote',
                    classes: ['side-quote']
                },
                {
                    name: 'Marker',
                    element: 'span',
                    classes: ['marker']
                },
                {
                    name: 'Spoiler',
                    element: 'span',
                    classes: ['spoiler']
                },
                {
                    name: 'Code (dark)',
                    element: 'pre',
                    classes: ['fancy-code', 'fancy-code-dark']
                },
                {
                    name: 'Code (bright)',
                    element: 'pre',
                    classes: ['fancy-code', 'fancy-code-bright']
                }
            ]
        },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
        }
    };

    const onGoToMasters = () => {
        if (projectName) {
            navigate(`/${projectName}/masters`);
        }
    }

    useEffect(() => {
        const driveDetails = location?.state?.drive;
        const courseDetails = location?.state?.course;
        const configurationId = location?.state?.configurationId;
        if (!driveDetails || !courseDetails || !configurationId) {
            onGoToMasters();
        } else {
            setDrive(() => driveDetails);
            setCourse(() => courseDetails);

            const fetchConfiguration = async () => {
                try {
                    const res = await getById(configurationId);
                    if (res.code === 'OK') {
                        setConfiguration(res.data);
                        const firstLanguage = res.data.languages[0];
                        const instructionIndex = res.data.instructions.findIndex((i) => i.language === firstLanguage);
                        const currentInstruction = res.data.instructions[instructionIndex]?.value ?? '';
                        if (res.data.instructionUploadType === 'WYSIWYG') {
                            setInitInstruction(currentInstruction);
                        } else {
                            setInstruction(currentInstruction);
                        }
                        setLoaded(true);
                    }
                } catch (error) {
                    console.log('There is an error while fetching configuration', error);
                }
            }
            fetchConfiguration();
        }
    }, [location, navigate]);


    const handleChange = (e, value) => {
        setTabIndex(value);
        const language = configuration.languages[value];
        const instructionIndex = configuration.instructions.findIndex((i) => i.language === language);
        const currentInstruction = configuration.instructions[instructionIndex]?.value ?? '';
        if (configuration.instructionUploadType === 'WYSIWYG') {
            editor.setData(currentInstruction);
        } else {
            setInstruction(currentInstruction);
        }
    }

    const handleEditorChange = (event, editor) => {
        if (configuration.instructionUploadType === 'WYSIWYG') {
            const data = editor.getData();
            setInstruction(data);
        } else {
            setInstruction(event.target.value);
        }
    }
    const handleOnReady = (ed) => {
        setEditor(ed);
        if (initInstruction) {
            ed.setData(initInstruction);
        } else {
            ed.setData('');
        }
    }

    const handleSave = async () => {
        if (!instruction) {
            addNotification('Please enter some content before saving instruction.', 'danger');
        }
        try {
            const language = configuration.languages[tabIndex];
            let instructions = configuration.instructions ?? [];
            const instructionIndex = instructions.findIndex((i) => i.language === language);
            if (instructionIndex === -1) {
                instructions.push({
                    language: language,
                    value: instruction,
                });
            } else {
                instructions[instructionIndex].value = instruction;
            }
            const formBody = new FormData();
            formBody.append('configuration', JSON.stringify({ instructions }));
            const res = await updateById(configuration.id, formBody);
            if (res.code === 'OK') {
                addNotification('Instruction saved successfully', 'success');
                setConfiguration({
                    ...configuration,
                    instructions: instructions
                });
            }
        } catch (error) {
            console.log('There is an error while uploading instruction', error);
        }
        console.log(configuration);
    }

    const handleInstructionUploadTypeChange = async (value) => {
        if (!['WYSIWYG', 'TEXT'].includes(value)) return;
        setLoaded(false);
        const formBody = new FormData();
        formBody.append('configuration', JSON.stringify({ instructionUploadType: value, instructions: [] }))
        const res = await updateById(configuration.id, formBody);
        if (res.code === 'OK') {
            addNotification('Instruction upload method changed successfully', 'success');
            setConfiguration({
                ...configuration,
                instructionUploadType: value,
                instructions: []
            });
            setInstruction('');
            setInitInstruction('');
            setLoaded(true);
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>Instructions Upload</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div className="search-header-wrapper" sx={{ mb: 1.5 }}>
                    <h1 className='title' style={{
                        margin: '0px',
                        marginRight: 'auto',
                        textTransform: 'capitalize'
                    }}>Instructions Upload</h1>
                </div>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    rowGap: 0.5,
                    mb: 1.5
                }}>
                    <TextField
                        label="Drive Name"
                        variant="outlined"
                        value={drive['driveName']}
                        disabled
                        InputLabelProps={{
                            shrink: true
                        }}
                        sx={{
                            width: 238,
                            bgcolor: 'background.base'
                        }}
                    />
                    <TextField
                        label="Course Name"
                        variant="outlined"
                        value={course['courseName']}
                        disabled
                        InputLabelProps={{
                            shrink: true
                        }}
                        sx={{
                            width: 238,
                            bgcolor: 'background.base'
                        }}
                    />
                    <RadiusButton
                        variant='contained'
                        color='grey'
                        sx={{ ml: 'auto' }}
                        onClick={onGoToMasters}
                        startIcon={<img src={ARROW_LEFT} alt='' />}
                    >
                        Go back
                    </RadiusButton>
                </Box>

                <Box sx={{
                    display: 'flex',
                    gap: 2.5,
                    mb: 1.5,
                }}>
                    <Typography variant='subtitle1' sx={{ lineHeight: 2.45 }}>Instructions upload type</Typography>
                    <RadioGroup row name="exam-type" sx={{
                        display: 'flex',
                        alignItems: 'flex-start'
                    }} onChange={(e, value) => handleInstructionUploadTypeChange(value)}>
                        <FormControlLabel value='WYSIWYG' control={
                            <Radio
                                size='small'
                                checked={configuration?.instructionUploadType === 'WYSIWYG'}
                            />
                        } label="WYSIWYG" />
                        <FormControlLabel value='TEXT' control={
                            <Radio
                                size='small'
                                checked={configuration?.instructionUploadType === 'TEXT'}
                            />
                        } label="HTML" />
                    </RadioGroup>
                </Box>

                <Paper sx={{
                    borderRadius: 3,
                    bgcolor: 'background.base',
                    flex: 1,
                    maxWidth: 'calc(100vw - 140px)',
                    overflow: 'auto',
                }}>
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            borderBottom: 1,
                            borderColor: 'divider'
                        }}>
                            <Tabs variant='scrollable' value={tabIndex} onChange={handleChange}>
                                {configuration?.languages.map((language) => {
                                    return <Tab
                                        key={language}
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontSize: 15,
                                            fontWeight: 600
                                        }}
                                        label={language}
                                        id={'tabpanel' + language}
                                        aria-controls={'tabpanel' + language}
                                    />
                                })}
                            </Tabs>
                            <RadiusButton
                                variant='contained'
                                color='secondary'
                                sx={{ ml: 'auto', mr: 2 }}
                                onClick={handleSave}
                            >
                                Save
                            </RadiusButton>
                        </Box>
                        <Box
                            role="tabpanel"
                            id='tabpanel'
                            sx={{ overflow: 'auto' }}
                        >
                            {loaded && <>

                                {configuration?.instructionUploadType === 'WYSIWYG'
                                    ? <CKEditor
                                        style={{ overflow: 'auto', maxHeight: '200px' }}
                                        editor={ClassicEditor}
                                        config={editorConfig}
                                        onReady={handleOnReady}
                                        onChange={handleEditorChange}
                                    />
                                    : <TextField
                                        id="multilineInput"
                                        multiline
                                        rows={17}
                                        value={instruction}
                                        fullWidth
                                        onChange={handleEditorChange}
                                        placeholder="Enter instructions here."
                                    />}
                            </>}

                        </Box>
                    </Box>
                </Paper>
            </Container>
        </MainContainer >

    );
}